import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { BookingDocket } from "../components/BookingDocket";
import { Icon } from "../components/Icon";
import { LoadingState } from "../components/LoadingState";
import { SiteHeader } from "../components/SiteHeader";
import { Turnstile } from "../components/Turnstile";
import { createBooking, defaultBookingRange, getAvailability, getPublicBookingPage } from "../lib/api";
import {
  browserTimeZone,
  dateRangeKeys,
  formatDay,
  formatFullDateTime,
  formatTime,
  practicalTimeZones,
  toDateKey,
} from "../lib/date";
import { downloadBookingIcs, googleCalendarUrl } from "../lib/ics";
import { BookingApiError, type AvailableSlot, type BookingConfirmation, type PublicBookingPage } from "../lib/types";

type GuestForm = {
  name: string;
  email: string;
  notes: string;
  consent: boolean;
  companyWebsite: string;
};

const initialGuest: GuestForm = { name: "", email: "", notes: "", consent: false, companyWebsite: "" };

export function BookPage() {
  const { eventSlug = "shahil-30" } = useParams();
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState<PublicBookingPage | null>(null);
  const [slots, setSlots] = useState<AvailableSlot[]>([]);
  const [timeZone, setTimeZone] = useState(() => searchParams.get("tz") || browserTimeZone);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<AvailableSlot | null>(null);
  const [guest, setGuest] = useState(initialGuest);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<BookingConfirmation | null>(null);
  const formHeadingRef = useRef<HTMLHeadingElement>(null);
  const confirmationHeadingRef = useRef<HTMLHeadingElement>(null);
  const idempotencyKeyRef = useRef(crypto.randomUUID());
  const range = useMemo(() => defaultBookingRange(), []);
  const dates = useMemo(() => dateRangeKeys(14, timeZone), [timeZone]);

  const loadAvailability = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [metadata, available] = await Promise.all([
        getPublicBookingPage(eventSlug),
        getAvailability(eventSlug, range.from, range.to),
      ]);
      setPage(metadata);
      setSlots(available);
      const firstDate = dates.find((date) => available.some((slot) => toDateKey(new Date(slot.starts_at), timeZone) === date));
      setSelectedDate((current) => current ?? firstDate ?? dates[0] ?? null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Live availability could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [dates, eventSlug, range.from, range.to, timeZone]);

  useEffect(() => {
    void loadAvailability();
  }, [loadAvailability]);

  const selectedDaySlots = useMemo(
    () => slots.filter((slot) => selectedDate && toDateKey(new Date(slot.starts_at), timeZone) === selectedDate),
    [selectedDate, slots, timeZone],
  );

  const chooseSlot = (slot: AvailableSlot) => {
    if (slot.starts_at !== selectedSlot?.starts_at) idempotencyKeyRef.current = crypto.randomUUID();
    setSelectedSlot(slot);
    setError(null);
    window.requestAnimationFrame(() => formHeadingRef.current?.focus());
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedSlot || !guest.consent) return;
    setSubmitting(true);
    setError(null);
    try {
      const booked = await createBooking({
        event_slug: eventSlug,
        starts_at: selectedSlot.starts_at,
        guest_name: guest.name.trim(),
        guest_email: guest.email.trim().toLowerCase(),
        guest_timezone: timeZone,
        guest_notes: guest.notes.trim(),
        consented_at: new Date().toISOString(),
        idempotency_key: idempotencyKeyRef.current,
        turnstile_token: turnstileToken,
        company_website: guest.companyWebsite,
      });
      setConfirmation(booked);
      window.requestAnimationFrame(() => confirmationHeadingRef.current?.focus());
    } catch (caught) {
      if (caught instanceof BookingApiError && caught.code === "slot_unavailable") {
        idempotencyKeyRef.current = crypto.randomUUID();
        setSelectedSlot(null);
        setError("That time was just booked. Choose another available time.");
        await loadAvailability();
      } else {
        setError(caught instanceof Error ? caught.message : "The booking could not be completed.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmation) {
    return (
      <div className="booking-page booking-page--confirmed">
        <SiteHeader quiet />
        <main className="confirmation-shell">
          <section className="confirmation-copy" aria-labelledby="confirmation-title">
            <p className="section-label">Confirmed · {confirmation.reference}</p>
            <h1 id="confirmation-title" ref={confirmationHeadingRef} tabIndex={-1}>You&rsquo;re booked.</h1>
            <p>
              The meeting is in RSM&rsquo;s central dashboard. Keep the private link below if you need to change it.
            </p>
            <div className="confirmation-actions">
              <button className="button button--primary" type="button" onClick={() => downloadBookingIcs(confirmation)}>
                <Icon name="download" /> Download calendar file
              </button>
              <a className="button button--outline" href={googleCalendarUrl(confirmation)} target="_blank" rel="noreferrer">
                Google Calendar <Icon name="external" />
              </a>
            </div>
            <Link className="manage-link" to={`/manage/${confirmation.manage_token}`}>
              Reschedule or cancel this meeting <Icon name="arrow" />
            </Link>
          </section>
          <BookingDocket booking={confirmation} viewerTimeZone={timeZone} />
        </main>
      </div>
    );
  }

  return (
    <div className="booking-page">
      <SiteHeader quiet />
      <main className="booking-shell">
        <aside className="booking-context" aria-label="Meeting details">
          <Link className="back-link" to="/"><span aria-hidden="true">←</span> Back to RSM</Link>
          {page ? (
            <>
              <p className="section-label">{page.host.domain_label}</p>
              <h1>{page.event_type.title}</h1>
              <p className="booking-context__host">with {page.host.display_name}</p>
              <p className="booking-context__description">{page.event_type.description}</p>
              <dl className="booking-facts">
                <div><dt><Icon name="clock" /> Duration</dt><dd>{page.event_type.duration_minutes} minutes</dd></div>
                <div><dt><Icon name="calendar" /> Format</dt><dd>{page.event_type.location_label}</dd></div>
              </dl>
            </>
          ) : loading ? <LoadingState label="Loading meeting" /> : null}
        </aside>

        <section className="booking-picker" aria-labelledby="booking-picker-title">
          <div className="booking-picker__heading">
            <div>
              <p className="section-label">Your calendar</p>
              <h2 id="booking-picker-title">Choose a date and time</h2>
            </div>
            <label className="timezone-control">
              <span>Timezone</span>
              <select value={timeZone} onChange={(event) => { setTimeZone(event.target.value); setSelectedSlot(null); setSelectedDate(null); }}>
                {practicalTimeZones.map((zone) => <option value={zone} key={zone}>{zone.replaceAll("_", " ")}</option>)}
              </select>
            </label>
          </div>

          {error ? <div className="error-banner" role="alert">{error}</div> : null}
          {loading ? <LoadingState label="Finding common time" /> : (
            <>
              <div className="date-strip" role="list" aria-label="Available dates">
                {dates.map((date) => {
                  const count = slots.filter((slot) => toDateKey(new Date(slot.starts_at), timeZone) === date).length;
                  return (
                    <button
                      className={`date-choice${selectedDate === date ? " is-selected" : ""}`}
                      type="button"
                      key={date}
                      onClick={() => { setSelectedDate(date); setSelectedSlot(null); }}
                      disabled={count === 0}
                      aria-pressed={selectedDate === date}
                    >
                      <span>{formatDay(date, timeZone).split(" ")[0]}</span>
                      <strong>{new Date(`${date}T12:00:00Z`).getUTCDate()}</strong>
                      <small>{count ? `${count} times` : "Full"}</small>
                    </button>
                  );
                })}
              </div>
              <div className="time-rail" aria-live="polite">
                <div className="time-rail__line" aria-hidden="true" />
                <p className="time-rail__date">{selectedDate ? formatDay(selectedDate, timeZone) : "Choose a day"}</p>
                {selectedDaySlots.length ? (
                  <div className="slot-list" aria-label="Available times">
                    {selectedDaySlots.map((slot) => (
                      <button
                        className={`slot${selectedSlot?.starts_at === slot.starts_at ? " is-selected" : ""}`}
                        type="button"
                        key={slot.starts_at}
                        onClick={() => chooseSlot(slot)}
                        aria-pressed={selectedSlot?.starts_at === slot.starts_at}
                      >
                        <span className="slot__local">{formatTime(slot.starts_at, timeZone)}</span>
                        {timeZone !== "Africa/Johannesburg" ? <span className="slot__sast">{formatTime(slot.starts_at, "Africa/Johannesburg")} SAST</span> : null}
                      </button>
                    ))}
                  </div>
                ) : <p className="empty-state">No times remain on this day. Try the next available date.</p>}
              </div>
            </>
          )}
        </section>

        <aside className="booking-review" aria-label="Booking review">
          <BookingDocket page={page ?? undefined} startAt={selectedSlot?.starts_at} viewerTimeZone={timeZone} compact />
          {selectedSlot && page ? (
            <form className="guest-form" onSubmit={submit} noValidate>
              <h2 ref={formHeadingRef} tabIndex={-1}>Your details</h2>
              <p className="guest-form__time">{formatFullDateTime(selectedSlot.starts_at, timeZone)}</p>
              <label>
                <span>Name</span>
                <input required autoComplete="name" maxLength={100} value={guest.name} onChange={(event) => setGuest({ ...guest, name: event.target.value })} />
              </label>
              <label>
                <span>Email</span>
                <input required type="email" autoComplete="email" maxLength={254} value={guest.email} onChange={(event) => setGuest({ ...guest, email: event.target.value })} />
              </label>
              <label>
                <span>What should we prepare? <small>Optional</small></span>
                <textarea rows={3} maxLength={1200} value={guest.notes} onChange={(event) => setGuest({ ...guest, notes: event.target.value })} />
              </label>
              <label className="honeypot" aria-hidden="true">
                Company website
                <input tabIndex={-1} autoComplete="off" value={guest.companyWebsite} onChange={(event) => setGuest({ ...guest, companyWebsite: event.target.value })} />
              </label>
              <label className="consent-row">
                <input required type="checkbox" checked={guest.consent} onChange={(event) => setGuest({ ...guest, consent: event.target.checked })} />
                <span>Use my details to arrange this meeting. <Link to="/privacy" target="_blank">Privacy notice</Link></span>
              </label>
              <Turnstile onToken={setTurnstileToken} />
              <button className="button button--primary button--wide" type="submit" disabled={submitting || !guest.name.trim() || !guest.email.trim() || !guest.consent}>
                {submitting ? "Booking…" : `Book ${formatTime(selectedSlot.starts_at, timeZone)}`} <Icon name="arrow" />
              </button>
            </form>
          ) : <p className="booking-review__hint">Choose a time to complete your booking.</p>}
        </aside>
      </main>
    </div>
  );
}
